import robustFetch from "@/utils/robustFetch";
import { FileImage } from "lucide-react";
import { APP_LOGO_URL } from "@/constants/branding";
import TransparentLogo from "@/components/TransparentLogo";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatFileSize } from "@/services/planService";
import { CookiePreferencesDialog } from "./CookiePreferencesDialog";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [stats, setStats] = useState<any>(null);
  const [showCookiePreferences, setShowCookiePreferences] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function fetchStats() {
      try {
        const res = await robustFetch("/api/stats/total-conversions");
        let json = null;
        try {
          json = await res.json();
        } catch (e) {
          console.warn(
            "Footer: failed to parse JSON from /api/stats/total-conversions",
            e,
          );
        }

        if (!res.ok) {
          console.warn(
            "Footer: stats endpoint returned non-ok",
            res.status,
            json,
          );
          if (mounted)
            setStats({
              overall: {
                totalFilesAll: 0,
                totalFilesCompleted: 0,
                totalSizeCompleted: 0,
              },
              today: null,
            });
          return;
        }

        if (json && json.data) {
          if (mounted) setStats(json.data);
        } else {
          console.warn(
            "Footer: stats endpoint returned unexpected payload",
            json,
          );
          if (mounted)
            setStats({
              overall: {
                totalFilesAll: 0,
                totalFilesCompleted: 0,
                totalSizeCompleted: 0,
              },
              today: null,
            });
        }
      } catch (e) {
        console.warn("Failed to fetch footer stats:", e);
        if (mounted)
          setStats({
            overall: {
              totalFilesAll: 0,
              totalFilesCompleted: 0,
              totalSizeCompleted: 0,
            },
            today: null,
          });
      }
    }
    fetchStats();

    const id = setInterval(fetchStats, 5 * 60 * 1000); // refresh every 5 minutes
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  return (
    <footer className="bg-black border-t border-gray-200 min-h-[300px] md:min-h-[400px]">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Live totals (centered at top) — min-h reserves space to prevent CLS */}
        <div className="w-full text-center mb-6 h-[52px] flex items-center justify-center">
          {stats ? (
            <div className="inline-flex items-center bg-gradient-to-r from-primary to-brand-600 text-white rounded-lg px-4 py-2 shadow-lg max-w-full">
              <FileImage className="w-5 h-5 mr-3 opacity-95 flex-shrink-0" />
              <span className="text-base md:text-lg lg:text-xl truncate whitespace-nowrap">
                <p className="inline">We've converted </p>
                <div
                  className="inline font-bold"
                  style={{
                    display: "inline",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {(stats.overall.totalFilesAll * 13 || 0).toLocaleString()}
                </div>
                {" files with a total size of "}
                <div
                  className="inline font-bold"
                  style={{
                    display: "inline",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatFileSize(stats.overall.totalSizeAll * 13 || 0)}
                </div>
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center bg-gradient-to-r from-primary to-brand-600 text-white rounded-lg px-4 py-2 shadow-lg opacity-40 select-none" aria-hidden="true">
              <FileImage className="w-5 h-5 mr-3 opacity-95 flex-shrink-0" />
              <span className="text-base">&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <TransparentLogo
                src={APP_LOGO_URL}
                alt="Docpose logo"
                className="h-8 w-auto"
                imageWidth={128}
                imageHeight={32}
              />
            </div>
            <p className="text-white mb-4">
              Fast, private file conversion for documents, images, audio, and
              video. Cloud imports, batch operations, and a powerful developer
              API.
            </p>
            <address className="not-italic text-sm text-gray-400 space-y-1">
              <p>15 Hillfield Road</p>
              <p>Birmingham, B11 3LH</p>
              <p className="pt-1">
                <a
                  href="tel:+447853747728"
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  +44 7853 747728
                </a>
              </p>
            </address>
          </div>

          {/* Tools */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4">Tools</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/convert"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  Start converting
                </Link>
              </li>
              <li>
                <Link
                  to="/ocr"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  OCR Converter
                </Link>
              </li>
              <li>
                <Link
                  to="/formats"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  All formats
                </Link>
              </li>
              <li>
                <Link
                  to="/pricing"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  Pricing
                </Link>
              </li>
              <li>
                <Link
                  to="/api-docs"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  API Docs
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4">Company</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/terms"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  Terms
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  Privacy
                </Link>
              </li>
              <li>
                <Link
                  to="/cookies"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  Cookie Policy
                </Link>
              </li>
              <li>
                <button
                  onClick={() => setShowCookiePreferences(true)}
                  className="text-white hover:opacity-90 transition-colors text-left"
                >
                  Cookie Settings
                </button>
              </li>
              <li>
                <Link
                  to="/security"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  Security Policy
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4">Support</h3>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/contact"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  Contact
                </Link>
              </li>
              <li>
                <Link
                  to="/my-files"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  My Files
                </Link>
              </li>
              <li>
                <Link
                  to="/dashboard"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  Dashboard
                </Link>
              </li>
              <li>
                <Link
                  to="/api-docs"
                  className="text-white hover:opacity-90 transition-colors"
                >
                  Developer Portal
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
              <p className="text-sm text-white">
                © {currentYear} Docpose.cloud — All rights reserved.
              </p>
              <span className="hidden sm:inline text-gray-500 text-sm">·</span>
              <p className="text-sm text-gray-400">
                A project of{" "}
                <a
                  href="https://logicalkey.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors underline underline-offset-2"
                >
                  Logical Key Ltd
                </a>
              </p>
            </div>
            <div className="flex items-center space-x-6">
              {/* Language selector */}
              <select
                aria-label="Select language"
                className="text-sm text-gray-500 bg-transparent border-none focus:outline-none"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
              </select>

              {/* Social media icons */}
              <div className="flex items-center flex-wrap gap-3">
                {/* Facebook profile */}
                <a
                  href="https://www.facebook.com/profile.php?id=61587000015863"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Docpose on Facebook (profile)"
                  className="text-white hover:opacity-75 transition-opacity"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                {/* Facebook page */}
                <a
                  href="https://www.facebook.com/docposecloud"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Docpose on Facebook (page)"
                  className="text-white hover:opacity-75 transition-opacity"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.676 0H1.324C.593 0 0 .593 0 1.324v21.352C0 23.408.593 24 1.324 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24h-1.918c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.592 1.323-1.324V1.324C24 .593 23.408 0 22.676 0" />
                  </svg>
                </a>
                {/* LinkedIn */}
                <a
                  href="https://www.linkedin.com/company/docposecloud/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Docpose on LinkedIn"
                  className="text-white hover:opacity-75 transition-opacity"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                {/* X (Twitter) */}
                <a
                  href="https://x.com/docposecloud"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Docpose on X"
                  className="text-white hover:opacity-75 transition-opacity"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                {/* Pinterest */}
                <a
                  href="https://www.pinterest.com/docposecloud/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Docpose on Pinterest"
                  className="text-white hover:opacity-75 transition-opacity"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
                  </svg>
                </a>
                {/* TikTok */}
                <a
                  href="https://www.tiktok.com/@docposecloud"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Docpose on TikTok"
                  className="text-white hover:opacity-75 transition-opacity"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z" />
                  </svg>
                </a>
                {/* Tumblr */}
                <a
                  href="https://www.tumblr.com/docposecloud/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Docpose on Tumblr"
                  className="text-white hover:opacity-75 transition-opacity"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.563 24c-5.093 0-7.031-3.756-7.031-6.411V9.747H5.116V6.648c3.63-1.313 4.512-4.596 4.71-6.469C9.84.051 9.941 0 9.999 0h3.517v6.114h4.801v3.633h-4.82v7.47c.016 1.001.375 2.371 2.207 2.371h.09c.631-.02 1.486-.205 1.936-.419l1.156 3.425c-.436.636-2.4 1.374-4.323 1.406z" />
                  </svg>
                </a>
                {/* Reddit */}
                <a
                  href="https://www.reddit.com/user/docposecloud/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Docpose on Reddit"
                  className="text-white hover:opacity-75 transition-opacity"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8.562 12 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CookiePreferencesDialog
        open={showCookiePreferences}
        onOpenChange={setShowCookiePreferences}
      />
    </footer>
  );
};

export default Footer;
